import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Alert, Button, Form } from 'react-bootstrap'
import { continuationRequest, continuationResult } from './survivorContinuationActions'

const names = players => players.map(p => `${p.firstName || ''} ${p.lastName || ''}`.trim()).join(', ') || 'None'
const SurvivorContinuationAdmin = ({ contest, seasonId, onSaved }) => {
  const dispatch = useDispatch()
  const [phase, setPhase] = useState('Regular')
  const [week, setWeek] = useState('6')
  const [reason, setReason] = useState('')
  const [preview, setPreview] = useState(null)
  const [record, setRecord] = useState(null)
  const [removalPreview, setRemovalPreview] = useState(null)
  const [removalReason, setRemovalReason] = useState('')
  const [message, setMessage] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const lock = useRef(false)
  const currentSeason = useRef(seasonId)
  currentSeason.current = seasonId
  const load = async () => {
    const id = seasonId
    const result = continuationResult(await dispatch(continuationRequest(contest, id)))
    if (currentSeason.current === id) { setRecord(result.continuation); setLoaded(true) }
  }
  useEffect(() => {
    setLoaded(false); setRecord(null); setPreview(null); setRemovalPreview(null); setRemovalReason(''); setMessage(''); setError('')
    if (seasonId) load().catch(() => setError('Unable to load continuation settings. Reload this page.'))
  }, [contest, seasonId])
  const run = async confirm => {
    if (lock.current || !loaded) return
    lock.current = true; setBusy(true); setError(''); setMessage('')
    const request = { seasonId, seasonMode: phase, week: Number(week), reason, ...(confirm ? { token: preview.token } : {}) }
    try {
      const result = continuationResult(await dispatch(continuationRequest(contest, confirm ? 'Confirm' : 'Preview', request)))
      if (currentSeason.current !== seasonId) { lock.current = false; setBusy(false); return }
      if (confirm) {
        setPreview(null)
        await load()
        if (onSaved) await onSaved()
      } else setPreview(result)
    } catch (error) { setPreview(null); setError(error.message) }
    lock.current = false; setBusy(false)
  }
  const remove = async confirm => {
    if (lock.current || !loaded) return
    lock.current = true; setBusy(true); setError(''); setMessage('')
    let removed = false
    try {
      const request = { seasonId, reason: removalReason, ...(confirm ? { token: removalPreview.token } : {}) }
      const result = continuationResult(await dispatch(continuationRequest(contest, confirm ? 'RemoveConfirm' : 'RemovePreview', request)))
      if (currentSeason.current === seasonId) {
        if (confirm) {
          removed = true
          setRecord(null); setRemovalPreview(null); setPreview(null); setLoaded(false)
          setMessage(result.message || 'Continuation removed. Base-format scoring is restored.')
          await load()
          if (onSaved) await onSaved()
        } else setRemovalPreview(result)
      }
    } catch (error) {
      setRemovalPreview(null)
      setError(removed ? 'Continuation removed. Reload configuration to see the latest results.' : error.message.startsWith('The ') || error.message.startsWith('No active') || error.message.startsWith('Keep the removal') ? error.message : 'Unable to remove continuation. Reload and try again.')
    }
    lock.current = false; setBusy(false)
  }
  if (!seasonId) return null
  return <section className='mt-4' aria-label={`${contest} continuation`}>
    <h4>Continue contest with remaining players</h4>
    {error ? <Alert variant='danger'>{error}</Alert> : null}
    {message ? <Alert variant='success'>{message}</Alert> : null}
    {record ? <>
      <Alert variant='info'>{record.message}{record.reason ? <p>{record.reason}</p> : null}</Alert>
      <Form.Label htmlFor={`${contest}-removal-reason`}>Removal reason (optional)</Form.Label>
      <Form.Control id={`${contest}-removal-reason`} as='textarea' maxLength={1000} disabled={busy} value={removalReason} onChange={event => { setRemovalReason(event.target.value); setRemovalPreview(null) }} />
      <Button className='mt-2' type='button' variant='outline-danger' disabled={busy || !loaded} onClick={() => remove(false)}>Preview removal</Button>
      {removalPreview ? <Alert variant='warning' className='mt-3'>
        <p>{removalPreview.message}</p>
        <p><strong>Would remain in contention:</strong> {names(removalPreview.remainingPlayers)}</p>
        <p><strong>Would be out:</strong> {names(removalPreview.eliminatedPlayers)}</p>
        <p><strong>Newly out:</strong> {names(removalPreview.newlyEliminatedPlayers)}</p>
        <p>You can set up a fresh continuation afterward. This takes effect immediately.</p>
        <Button type='button' variant='danger' disabled={busy} onClick={() => remove(true)}>Remove continuation</Button>{' '}
        <Button type='button' variant='light' disabled={busy} onClick={() => setRemovalPreview(null)}>Cancel</Button>
      </Alert> : null}
    </> : <>
      <p>Use when all remaining players reached the contest’s loss limit in the same week. Continuation adds one loss allowance only for those players. Players eliminated earlier stay out. Actual picks and used teams are preserved.</p>
      <fieldset disabled={busy || !loaded}>
        <Form.Label htmlFor={`${contest}-continuation-phase`}>Phase of the final losing week</Form.Label>
        <Form.Select id={`${contest}-continuation-phase`} value={phase} onChange={e => { setPhase(e.target.value); setWeek('1'); setPreview(null) }}><option value='Regular'>Regular season</option><option value='Postseason'>Postseason</option></Form.Select>
        <Form.Label htmlFor={`${contest}-continuation-week`}>Final losing week</Form.Label>
        <Form.Control id={`${contest}-continuation-week`} type='number' min='1' max={phase === 'Regular' ? 18 : 4} value={week} onChange={e => { setWeek(e.target.value); setPreview(null) }} />
        <Form.Label htmlFor={`${contest}-continuation-reason`}>Explanation for players (optional)</Form.Label>
        <Form.Control id={`${contest}-continuation-reason`} as='textarea' maxLength={1000} value={reason} onChange={e => { setReason(e.target.value); setPreview(null) }} />
        <Button className='mt-2' type='button' onClick={() => run(false)}>{busy ? 'Working…' : 'Preview continuation'}</Button>
        {preview ? <Alert variant='warning' className='mt-3'>
          <p><strong>Continue with a {preview.maxLosses}-loss limit:</strong> {names(preview.eligiblePlayers)}</p>
          <p><strong>Remain eliminated:</strong> {names(preview.previouslyEliminatedPlayers)}</p>
          <p>The selected week’s losses count. Confirming records this roster for the season and takes effect immediately, independently of other configuration edits.</p>
          <Button type='button' onClick={() => run(true)}>Confirm continuation</Button>
        </Alert> : null}
      </fieldset>
    </>}
  </section>
}
export default SurvivorContinuationAdmin
